import _extends from "@babel/runtime/helpers/extends";
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl-next';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { fg } from '@atlaskit/platform-feature-flags';
import { G300, R400 } from '@atlaskit/theme/colors';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { messages } from './messages';
export const onCopyClick = (formToCopy, projects, templateFormApi, showFlag, reloadProjectForms, onClose, reportError, createAnalyticsEvent) => {
  const projectsToCopyTo = projects.filter(project => project.selected);
  const promises = [];
  const projectsSucceeded = [];
  const projectsFailed = [];
  projectsToCopyTo.forEach(project => {
    promises.push(templateFormApi.copyTemplateForm(formToCopy.id, project.id,
    // project ID to copy the form to
    formToCopy.projectId,
    // project ID to copy the form from
    formToCopy.name).then(() => {
      projectsSucceeded.push(project);
      if (fg('proforma_add_project_form_analytics')) {
        fireTrackAnalytics(createAnalyticsEvent({}), `${ActionSubject.FORM} ${Action.COPIED}`, {
          templateFormId: formToCopy.id,
          projectCopiedFromId: formToCopy.projectId,
          projectCopiedToId: project.id
        });
      }
    }).catch(error => {
      projectsFailed.push(project);
      if (fg('proforma_add_project_form_analytics')) {
        reportError(error, {
          id: 'copyForm'
        });
      }
    }));
  });
  Promise.all(promises).finally(() => {
    const formName = formToCopy.name;
    if (projectsFailed.length > 0) {
      showFlag({
        appearance: 'error',
        id: 'copy-form-error-flag',
        icon: /*#__PURE__*/React.createElement(ErrorIcon, {
          label: "error",
          secondaryColor: `var(--ds-icon-danger, ${R400})`
        }),
        title: /*#__PURE__*/React.createElement(FlagHeading, null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.copyErrorTitle, {
          values: {
            formName
          }
        }))),
        isAutoDismiss: true
      });
    }
    if (projectsSucceeded.length > 0) {
      const numberOfProjects = projectsSucceeded.length;
      if (numberOfProjects < 1) {
        return; // Theoretically, not possible.
      }
      const serviceProjects = projectsSucceeded.filter(project => project.projectTypeKey === 'service_desk');
      const numberOfServiceProjects = serviceProjects.length;
      const softwareProjects = projectsSucceeded.filter(project => project.projectTypeKey === 'software');
      const numberOfSoftwareProjects = softwareProjects.length;
      const businessProjects = projectsSucceeded.filter(project => project.projectTypeKey === 'business');
      const numberOfBusinessProjects = businessProjects.length;
      let titleMessage;
      let titleValues;
      let descriptionMessage;
      let descriptionValues;
      if (numberOfProjects !== numberOfServiceProjects + numberOfSoftwareProjects + numberOfBusinessProjects) {
        // There is at least one project with an unrecognised type.
        if (numberOfProjects > 1) {
          titleMessage = messages.copySuccessUnrecognisedProjectsTitle;
          titleValues = {
            numberOfProjects
          };
          descriptionMessage = messages.copySuccessUnrecognisedProjectsDescription;
          descriptionValues = {
            formName,
            numberOfProjects
          };
        } else {
          // numberOfProjects === 1
          titleMessage = messages.copySuccessUnrecognisedProjectsTitle;
          titleValues = {
            numberOfProjects
          };
          descriptionMessage = messages.copySuccessOneProjectDescription;
          descriptionValues = {
            formName,
            projectName: projectsSucceeded[0].name
          };
        }
      } else if (numberOfServiceProjects > 0) {
        if (numberOfSoftwareProjects > 0) {
          if (numberOfBusinessProjects > 0) {
            titleMessage = messages.copySuccessMultipleProjectTypesTitle;
            titleValues = {
              numberOfProjects
            };
            descriptionMessage = messages.copySuccessServiceSoftwareAndBusinessProjectsDescription;
            descriptionValues = {
              formName,
              numberOfServiceProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfServiceProjects, {
                values: {
                  numberOfServiceProjects
                }
              })),
              numberOfSoftwareProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfSoftwareProjects, {
                values: {
                  numberOfSoftwareProjects
                }
              })),
              numberOfBusinessProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfBusinessProjects, {
                values: {
                  numberOfBusinessProjects
                }
              }))
            };
          } else {
            titleMessage = messages.copySuccessMultipleProjectTypesTitle;
            titleValues = {
              numberOfProjects
            };
            descriptionMessage = messages.copySuccessServiceAndSoftwareProjectsDescription;
            descriptionValues = {
              formName,
              numberOfServiceProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfServiceProjects, {
                values: {
                  numberOfServiceProjects
                }
              })),
              numberOfSoftwareProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfSoftwareProjects, {
                values: {
                  numberOfSoftwareProjects
                }
              }))
            };
          }
        } else if (numberOfBusinessProjects > 0) {
          titleMessage = messages.copySuccessMultipleProjectTypesTitle;
          titleValues = {
            numberOfProjects
          };
          descriptionMessage = messages.copySuccessServiceAndBusinessProjectsDescription;
          descriptionValues = {
            formName,
            numberOfServiceProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfServiceProjects, {
              values: {
                numberOfServiceProjects
              }
            })),
            numberOfBusinessProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfBusinessProjects, {
              values: {
                numberOfBusinessProjects
              }
            }))
          };
        } else if (numberOfServiceProjects > 1) {
          titleMessage = messages.copySuccessServiceProjectsTitle;
          titleValues = {
            numberOfServiceProjects
          };
          descriptionMessage = messages.copySuccessServiceProjectsDescription;
          descriptionValues = {
            formName,
            numberOfServiceProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfServiceProjects, {
              values: {
                numberOfServiceProjects
              }
            }))
          };
        } else {
          // numberOfServiceProjects === 1
          titleMessage = messages.copySuccessServiceProjectsTitle;
          titleValues = {
            numberOfServiceProjects
          };
          descriptionMessage = messages.copySuccessOneProjectDescription;
          descriptionValues = {
            formName,
            projectName: serviceProjects[0].name
          };
        }
      } else if (numberOfSoftwareProjects > 0) {
        if (numberOfBusinessProjects > 0) {
          titleMessage = messages.copySuccessMultipleProjectTypesTitle;
          titleValues = {
            numberOfProjects
          };
          descriptionMessage = messages.copySuccessSoftwareAndBusinessProjectsDescription;
          descriptionValues = {
            formName,
            numberOfSoftwareProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfSoftwareProjects, {
              values: {
                numberOfSoftwareProjects
              }
            })),
            numberOfBusinessProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfBusinessProjects, {
              values: {
                numberOfBusinessProjects
              }
            }))
          };
        } else if (numberOfSoftwareProjects > 1) {
          titleMessage = messages.copySuccessSoftwareProjectsTitle;
          titleValues = {
            numberOfSoftwareProjects
          };
          descriptionMessage = messages.copySuccessSoftwareProjectsDescription;
          descriptionValues = {
            formName,
            numberOfSoftwareProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfSoftwareProjects, {
              values: {
                numberOfSoftwareProjects
              }
            }))
          };
        } else {
          // numberOfSoftwareProjects === 1
          titleMessage = messages.copySuccessSoftwareProjectsTitle;
          titleValues = {
            numberOfSoftwareProjects
          };
          descriptionMessage = messages.copySuccessOneProjectDescription;
          descriptionValues = {
            formName,
            projectName: softwareProjects[0].name
          };
        }
      } else if (numberOfBusinessProjects > 1) {
        titleMessage = messages.copySuccessBusinessProjectsTitle;
        titleValues = {
          numberOfBusinessProjects
        };
        descriptionMessage = messages.copySuccessBusinessProjectsDescription;
        descriptionValues = {
          formName,
          numberOfBusinessProjectsMessage: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.numberOfBusinessProjects, {
            values: {
              numberOfBusinessProjects
            }
          }))
        };
      } else {
        // numberOfBusinessProjects === 1
        titleMessage = messages.copySuccessBusinessProjectsTitle;
        titleValues = {
          numberOfBusinessProjects
        };
        descriptionMessage = messages.copySuccessOneProjectDescription;
        descriptionValues = {
          formName,
          projectName: businessProjects[0].name
        };
      }
      showFlag({
        id: 'copy-form-success-flag',
        icon: /*#__PURE__*/React.createElement(SuccessIcon, {
          label: "Success",
          primaryColor: `var(--ds-icon-success, ${G300})`
        }),
        title: /*#__PURE__*/React.createElement(FlagHeading, null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, titleMessage, {
          values: titleValues
        }))),
        description: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, descriptionMessage, {
          values: descriptionValues
        })),
        isAutoDismiss: true
      });
      if (projectsSucceeded.some(project => project.id === formToCopy.projectId)) {
        reloadProjectForms();
      }
    }
    onClose();
  });
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlagHeading = styled.h2({
  color: "var(--ds-text-subtle, #42526E)",
  fontSize: "var(--ds-font-body, 14px)",
  fontWeight: "var(--ds-font-weight-semibold, 600)",
  lineHeight: "var(--ds-space-250, 20px)",
  letterSpacing: 'normal'
});